import type { BridgeData, TornadoData, TrackData, Vec2, VehicleData, WaterwayData } from "../types";
import type { QuickRaceEngine, RaceParticipant } from "../core/race";
import { rgb } from "../core/math";
import { TrackRuntime } from "../core/track";
import { rabbitVisualState, type RabbitVisualState } from "./rabbitAnimation";
import { obstacleCorners } from "../core/obstacles";
import { bridgeRailHalfWidthAt } from "../core/bridge";
import standardVehicleSpriteUrl from "../../../assets/images/vehicle-standard-v1.png";
import aeroVehicleSpriteUrl from "../../../assets/images/vehicle-aero-v1.png";
import tankVehicleSpriteUrl from "../../../assets/images/vehicle-tank-v1.png";
import agileVehicleSpriteUrl from "../../../assets/images/vehicle-agile-v1.png";
import gardenGroundTextureUrl from "../../../assets/images/materials/dark-garden-ground-v1.png";

const vehicleSpriteUrls: Record<string, string> = {
  standard: standardVehicleSpriteUrl,
  aero: aeroVehicleSpriteUrl,
  tank: tankVehicleSpriteUrl,
  agile: agileVehicleSpriteUrl,
};
const vehicleSpriteCache = new Map<string, HTMLImageElement>();
const tintedVehicleSpriteCache = new Map<string, HTMLCanvasElement>();
const trackBackgroundCache = new Map<string, HTMLImageElement>();
const failedTrackBackgrounds = new Set<string>();

function vehicleSprite(vehicleKey: string, onReady: () => void): HTMLImageElement | null {
  const cached = vehicleSpriteCache.get(vehicleKey);
  if (cached) {
    if (cached.complete && cached.naturalWidth > 0) return cached;
    cached.addEventListener("load", onReady, { once: true });
    return null;
  }
  const source = vehicleSpriteUrls[vehicleKey];
  if (!source) return null;
  const image = new Image();
  image.decoding = "async";
  image.onload = onReady;
  image.src = source;
  vehicleSpriteCache.set(vehicleKey, image);
  return image.complete && image.naturalWidth > 0 ? image : null;
}

function trackBackground(source: string | undefined, onReady: () => void): HTMLImageElement | null {
  if (!source || failedTrackBackgrounds.has(source)) return null;
  const cached = trackBackgroundCache.get(source);
  if (cached) {
    if (cached.complete && cached.naturalWidth > 0) return cached;
    cached.addEventListener("load", onReady, { once: true });
    return null;
  }
  const image = new Image();
  image.decoding = "async";
  image.onload = onReady;
  image.onerror = () => failedTrackBackgrounds.add(source);
  image.src = source;
  trackBackgroundCache.set(source, image);
  return image.complete && image.naturalWidth > 0 ? image : null;
}

function drawSharedTrackBackground(
  context: CanvasRenderingContext2D,
  track: TrackRuntime,
  onReady: () => void,
): boolean {
  const image = trackBackground(track.data.background_image, onReady);
  if (!image) return false;
  context.drawImage(image, 0, 92, 940, 705);
  return true;
}

function drawInfiniteWilderness(
  context: CanvasRenderingContext2D,
  onReady: () => void,
): boolean {
  const image = trackBackground(gardenGroundTextureUrl, onReady);
  if (!image) return false;
  const pattern = context.createPattern(image, "repeat");
  if (!pattern) return false;
  // Den delte 940 x 705-bitmap er beskåret fra en 940 x 920-verden, som
  // selv bruger centrum af materialet. Samme oprindelse gør kanten usynlig.
  const cropX = Math.max(0, (image.naturalWidth - 940) / 2);
  const cropY = Math.max(0, (image.naturalHeight - 920) / 2);
  pattern.setTransform(new DOMMatrix().translate(-cropX, -cropY));
  context.fillStyle = pattern;
  context.fillRect(-5000, -5000, 10000, 10000);
  return true;
}

function drawSharedTrackForeground(
  context: CanvasRenderingContext2D,
  track: TrackRuntime,
  onReady: () => void,
): boolean {
  const image = trackBackground(track.data.foreground_image, onReady);
  if (!image) return false;
  context.drawImage(image, 0, 92, 940, 705);
  return true;
}

function drawSharedTrackRails(
  context: CanvasRenderingContext2D,
  track: TrackRuntime,
  onReady: () => void,
): boolean {
  const image = trackBackground(track.data.rails_image, onReady);
  if (!image) return false;
  context.drawImage(image, 0, 92, 940, 705);
  return true;
}

interface ViewTransform {
  scale: number;
  canvasCenter: Vec2;
  worldCenter: Vec2;
  rotation: number;
}

interface WorldBounds {
  minimum: Vec2;
  maximum: Vec2;
}

export function vehicleAltitudeScale(z: number): number {
  return 1 + Math.min(0.35, Math.max(0, z) / 80);
}

export function uprightRabbitTransform(heading: number): { facing: 1 | -1; tilt: number } {
  return {
    facing: Math.cos(heading) >= 0 ? 1 : -1,
    tilt: Math.max(-0.1, Math.min(0.1, Math.sin(heading) * 0.1)),
  };
}

function setupCanvas(canvas: HTMLCanvasElement): CanvasRenderingContext2D {
  const ratio = Math.min(window.devicePixelRatio || 1, 2);
  const width = Math.max(1, Math.floor(canvas.clientWidth));
  const height = Math.max(1, Math.floor(canvas.clientHeight));
  if (canvas.width !== Math.floor(width * ratio) || canvas.height !== Math.floor(height * ratio)) {
    canvas.width = Math.floor(width * ratio);
    canvas.height = Math.floor(height * ratio);
  }
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Canvas 2D is unavailable.");
  context.setTransform(ratio, 0, 0, ratio, 0, 0);
  return context;
}

function trackBounds(track: TrackRuntime, world: Vec2): WorldBounds {
  const margin = track.data.mean_width / 2 + 22;
  const xs = track.centerline.map((point) => point[0]);
  const ys = track.centerline.map((point) => point[1]);
  return {
    minimum: [
      Math.max(0, Math.min(...xs) - margin),
      Math.max(0, Math.min(...ys) - margin),
    ],
    maximum: [
      Math.min(world[0], Math.max(...xs) + margin),
      Math.min(world[1], Math.max(...ys) + margin),
    ],
  };
}

function transformFor(
  canvas: HTMLCanvasElement,
  bounds: WorldBounds,
  focus: Vec2 | null = null,
): ViewTransform {
  const padding = 18;
  const rotation = canvas.clientHeight > canvas.clientWidth * 1.12 ? Math.PI / 2 : 0;
  const boundsWidth = Math.max(1, bounds.maximum[0] - bounds.minimum[0]);
  const boundsHeight = Math.max(1, bounds.maximum[1] - bounds.minimum[1]);
  const orientedWidth = rotation === 0 ? boundsWidth : boundsHeight;
  const orientedHeight = rotation === 0 ? boundsHeight : boundsWidth;
  const overviewScale = Math.min(
    (canvas.clientWidth - padding * 2) / orientedWidth,
    (canvas.clientHeight - padding * 2) / orientedHeight,
  );
  return {
    scale: focus ? Math.max(canvas.clientWidth / orientedWidth, canvas.clientHeight / orientedHeight) : overviewScale,
    canvasCenter: [canvas.clientWidth / 2, canvas.clientHeight * (rotation === 0 ? 0.5 : 0.35)],
    worldCenter: focus ?? [
      (bounds.minimum[0] + bounds.maximum[0]) / 2,
      (bounds.minimum[1] + bounds.maximum[1]) / 2,
    ],
    rotation,
  };
}

function applyWorld(context: CanvasRenderingContext2D, transform: ViewTransform): void {
  context.translate(transform.canvasCenter[0], transform.canvasCenter[1]);
  context.scale(transform.scale, transform.scale);
  context.rotate(transform.rotation);
  context.translate(-transform.worldCenter[0], -transform.worldCenter[1]);
}

function pathClosed(context: CanvasRenderingContext2D, points: Vec2[]): void {
  const first = points[0];
  if (!first) return;
  context.beginPath();
  context.moveTo(first[0], first[1]);
  for (const point of points.slice(1)) context.lineTo(point[0], point[1]);
  context.closePath();
}

function strokeCenterline(context: CanvasRenderingContext2D, track: TrackRuntime, width: number, color: string): void {
  pathClosed(context, track.centerline);
  context.lineCap = "round";
  context.lineJoin = "round";
  context.lineWidth = width;
  context.strokeStyle = color;
  context.stroke();
}

function sampledTrackPath(track: TrackRuntime, startProgress: number, endProgress: number): Vec2[] {
  const distance = endProgress >= startProgress
    ? endProgress - startProgress
    : track.length - startProgress + endProgress;
  const steps = Math.max(2, Math.ceil(distance / 8));
  return Array.from({ length: steps + 1 }, (_, index) => (
    track.pointAt(startProgress + distance * index / steps).point
  ));
}

function strokeOpenPath(context: CanvasRenderingContext2D, points: Vec2[], width: number, color: string): void {
  const first = points[0];
  if (!first) return;
  context.beginPath();
  context.moveTo(first[0], first[1]);
  for (const point of points.slice(1)) context.lineTo(point[0], point[1]);
  context.lineCap = "butt";
  context.lineJoin = "round";
  context.lineWidth = width;
  context.strokeStyle = color;
  context.stroke();
}

function bridgeCorridorEdges(
  track: TrackRuntime,
  bridge: BridgeData,
  startProgress = bridge.start_progress,
  endProgress = bridge.end_progress,
  inset = 0,
): [Vec2[], Vec2[]] {
  const distance = endProgress >= startProgress
    ? endProgress - startProgress
    : track.length - startProgress + endProgress;
  const steps = Math.max(2, Math.ceil(distance / 6));
  const left: Vec2[] = [];
  const right: Vec2[] = [];
  for (let index = 0; index <= steps; index += 1) {
    const progress = startProgress + distance * index / steps;
    const sample = track.pointAt(progress);
    const width = Math.max(10, bridgeRailHalfWidthAt(bridge, progress) - inset);
    const normal: Vec2 = [-Math.sin(sample.heading), Math.cos(sample.heading)];
    left.push([sample.point[0] + normal[0] * width, sample.point[1] + normal[1] * width]);
    right.push([sample.point[0] - normal[0] * width, sample.point[1] - normal[1] * width]);
  }
  return [left, right];
}

function drawBridgeUnderlay(context: CanvasRenderingContext2D, track: TrackRuntime, bridge: BridgeData): void {
  if (bridge.underlay !== "water") return;
  if (track.waterways.length > 0) return;
  const middle = track.pointAt((bridge.start_progress + bridge.end_progress) / 2);
  const forward: Vec2 = [Math.cos(middle.heading), Math.sin(middle.heading)];
  const normal: Vec2 = [-forward[1], forward[0]];
  const halfLength = 132;
  const halfWidth = 34;
  const corners: Vec2[] = [
    [middle.point[0] - normal[0] * halfLength - forward[0] * halfWidth, middle.point[1] - normal[1] * halfLength - forward[1] * halfWidth],
    [middle.point[0] + normal[0] * halfLength - forward[0] * halfWidth, middle.point[1] + normal[1] * halfLength - forward[1] * halfWidth],
    [middle.point[0] + normal[0] * halfLength + forward[0] * halfWidth, middle.point[1] + normal[1] * halfLength + forward[1] * halfWidth],
    [middle.point[0] - normal[0] * halfLength + forward[0] * halfWidth, middle.point[1] - normal[1] * halfLength + forward[1] * halfWidth],
  ];
  pathClosed(context, corners);
  context.fillStyle = "#164e65";
  context.fill();
  pathClosed(context, corners);
  context.strokeStyle = "#78cadd";
  context.lineWidth = 3;
  context.stroke();
  for (let wave = -3; wave <= 3; wave += 1) {
    const center: Vec2 = [
      middle.point[0] + normal[0] * wave * 30,
      middle.point[1] + normal[1] * wave * 30,
    ];
    context.beginPath();
    context.moveTo(center[0] - forward[0] * 17 - normal[0] * 7, center[1] - forward[1] * 17 - normal[1] * 7);
    context.quadraticCurveTo(center[0], center[1] + 5, center[0] + forward[0] * 17 + normal[0] * 7, center[1] + forward[1] * 17 + normal[1] * 7);
    context.strokeStyle = "rgba(174, 236, 250, .58)";
    context.lineWidth = 2;
    context.stroke();
  }
}

function drawWaterway(context: CanvasRenderingContext2D, waterway: WaterwayData): void {
  const line = waterway.centerline;
  if (line.length < 2) return;
  const layers: Array<[number, string]> = [
    [waterway.width + 24, "#051819"],
    [waterway.width + 16, "#2b4030"],
    [waterway.width + 8, "#0d4852"],
    [waterway.width, "#188b9a"],
    [Math.max(5, waterway.width - 12), "#2fb7bf"],
  ];
  for (const [width, color] of layers) strokeOpenPath(context, line, width, color);
  for (let index = 0; index < line.length - 1; index += 1) {
    const start = line[index] as Vec2;
    const end = line[index + 1] as Vec2;
    const dx = end[0] - start[0];
    const dy = end[1] - start[1];
    const distance = Math.hypot(dx, dy);
    if (distance <= 1e-9) continue;
    const forward: Vec2 = [dx / distance, dy / distance];
    const normal: Vec2 = [-forward[1], forward[0]];
    for (const fraction of [0.24, 0.58]) {
      const center: Vec2 = [start[0] + dx * fraction, start[1] + dy * fraction];
      context.beginPath();
      context.moveTo(
        center[0] - forward[0] * 10 - normal[0] * waterway.width * .18,
        center[1] - forward[1] * 10 - normal[1] * waterway.width * .18,
      );
      context.lineTo(
        center[0] + forward[0] * 10 + normal[0] * waterway.width * .18,
        center[1] + forward[1] * 10 + normal[1] * waterway.width * .18,
      );
      context.strokeStyle = "#8be1da";
      context.lineWidth = 2;
      context.stroke();
    }
  }
}

function drawBridgeDeck(
  context: CanvasRenderingContext2D,
  track: TrackRuntime,
  bridge: BridgeData,
  flatOnly = false,
  includeRails = true,
): void {
  const start = flatOnly ? bridge.deck_start_progress : bridge.start_progress;
  const end = flatOnly ? bridge.deck_end_progress : bridge.end_progress;
  const [left, right] = bridgeCorridorEdges(track, bridge, start, end);
  const [innerLeft, innerRight] = bridgeCorridorEdges(track, bridge, start, end, 5);
  const outline = [...left, ...[...right].reverse()];
  const interior = [...innerLeft, ...[...innerRight].reverse()];
  if (!flatOnly) {
    context.save();
    context.translate(5, 8);
    pathClosed(context, outline);
    context.fillStyle = "rgba(2, 8, 9, .46)";
    context.fill();
    context.restore();
  }
  pathClosed(context, outline);
  context.fillStyle = "#061012";
  context.fill();
  pathClosed(context, interior);
  context.fillStyle = "#42484c";
  context.fill();
  if (!flatOnly) {
    for (const [rampStart, rampEnd, alpha] of [
      [bridge.start_progress, bridge.deck_start_progress, .17],
      [bridge.deck_end_progress, bridge.end_progress, .10],
    ] as const) {
      const [rampLeft, rampRight] = bridgeCorridorEdges(track, bridge, rampStart, rampEnd);
      pathClosed(context, [...rampLeft, ...[...rampRight].reverse()]);
      context.fillStyle = `rgba(124, 139, 143, ${alpha})`;
      context.fill();
    }
  }
  if (includeRails) drawBridgeRails(context, track, bridge);
}

function drawBridgeRails(context: CanvasRenderingContext2D, track: TrackRuntime, bridge: BridgeData): void {
  const [left, right] = bridgeCorridorEdges(track, bridge);
  for (const rail of [left, right]) {
    strokeOpenPath(context, rail, 9, "#0c1416");
    strokeOpenPath(context, rail, 3, "#ae9968");
    for (let index = 4; index < rail.length - 1; index += 4) {
      const point = rail[index]!;
      context.beginPath(); context.arc(point[0], point[1], 4, 0, Math.PI * 2);
      context.fillStyle = "#0a1214"; context.fill();
      context.beginPath(); context.arc(point[0], point[1], 2, 0, Math.PI * 2);
      context.fillStyle = "#d3c28b"; context.fill();
    }
  }
}

function drawCurbs(context: CanvasRenderingContext2D, boundary: Vec2[]): void {
  pathClosed(context, boundary);
  context.lineWidth = 7;
  context.lineCap = "butt";
  context.strokeStyle = "#f5eee3";
  context.stroke();
  context.setLineDash([18, 18]);
  context.strokeStyle = "#dc584e";
  context.stroke();
  context.setLineDash([]);
}

function drawStartLine(context: CanvasRenderingContext2D, track: TrackRuntime): void {
  const { point, heading } = track.pointAt(0);
  const normal: Vec2 = [-Math.sin(heading), Math.cos(heading)];
  const width = track.data.mean_width * 0.9;
  const start: Vec2 = [point[0] - normal[0] * width / 2, point[1] - normal[1] * width / 2];
  const square = 7;
  for (let row = 0; row < 2; row += 1) {
    for (let column = 0; column < Math.floor(width / square); column += 1) {
      context.save();
      context.translate(start[0] + normal[0] * (column * square + square / 2), start[1] + normal[1] * (column * square + square / 2));
      context.rotate(heading);
      context.fillStyle = (row + column) % 2 === 0 ? "#f5f1e9" : "#181d1d";
      context.fillRect(-square / 2, (row - 1) * square, square + 0.4, square + 0.4);
      context.restore();
    }
  }
}

function treeNoise(index: number, salt: number): number {
  const value = Math.sin(index * 91.733 + salt * 47.11) * 43758.5453;
  return value - Math.floor(value);
}

function drawEnvironment(context: CanvasRenderingContext2D, track: TrackRuntime, world: Vec2, bleed = false): void {
  const gradient = context.createLinearGradient(-world[0], -world[1], world[0] * 2, world[1] * 2);
  gradient.addColorStop(0, "#173b2b");
  gradient.addColorStop(0.55, "#123226");
  gradient.addColorStop(1, "#0c271e");
  context.fillStyle = gradient;
  context.fillRect(
    bleed ? -world[0] * 2 : 0,
    bleed ? -world[1] * 2 : 0,
    bleed ? world[0] * 5 : world[0],
    bleed ? world[1] * 5 : world[1],
  );
  context.fillStyle = "rgba(162, 211, 119, .055)";
  for (let x = 14; x < world[0]; x += 28) {
    for (let y = 14; y < world[1]; y += 28) {
      if ((x + y) % 84 === 0) context.fillRect(x, y, 2, 2);
    }
  }
  for (let index = 0; index < 115; index += 1) {
    const point: Vec2 = [26 + treeNoise(index, 1) * (world[0] - 52), 26 + treeNoise(index, 2) * (world[1] - 52)];
    if (track.contains(point, -34)) continue;
    const radius = 8 + treeNoise(index, 3) * 8;
    context.beginPath();
    context.ellipse(point[0] + 4, point[1] + 6, radius, radius * 0.75, 0, 0, Math.PI * 2);
    context.fillStyle = "rgba(2, 12, 9, .38)";
    context.fill();
    context.beginPath();
    context.arc(point[0], point[1], radius, 0, Math.PI * 2);
    context.fillStyle = index % 3 === 0 ? "#285d39" : "#205134";
    context.fill();
    context.beginPath();
    context.arc(point[0] - radius * 0.2, point[1] - radius * 0.25, radius * 0.55, 0, Math.PI * 2);
    context.fillStyle = "rgba(107, 170, 88, .35)";
    context.fill();
  }
  const flowerColors = ["#f3c64b", "#ef7e9d", "#a88ae8", "#f2eee5", "#72cde2"];
  for (let index = 0; index < 70; index += 1) {
    const point: Vec2 = [18 + treeNoise(index, 11) * (world[0] - 36), 18 + treeNoise(index, 12) * (world[1] - 36)];
    if (track.contains(point, -20)) continue;
    const size = 1.6 + treeNoise(index, 13) * 1.5;
    context.strokeStyle = "rgba(91, 145, 66, .8)";
    context.lineWidth = 0.9;
    context.beginPath(); context.moveTo(point[0], point[1] + 4); context.lineTo(point[0], point[1]); context.stroke();
    context.fillStyle = flowerColors[index % flowerColors.length] ?? "#f3c64b";
    for (let petal = 0; petal < 5; petal += 1) {
      const angle = petal * Math.PI * 2 / 5;
      context.beginPath();
      context.arc(point[0] + Math.cos(angle) * size, point[1] + Math.sin(angle) * size, size * 0.72, 0, Math.PI * 2);
      context.fill();
    }
    context.fillStyle = "#f4d45f";
    context.beginPath(); context.arc(point[0], point[1], size * 0.62, 0, Math.PI * 2); context.fill();
  }
}

function drawTrack(context: CanvasRenderingContext2D, track: TrackRuntime, viewRotation = 0): void {
  for (const waterway of track.waterways) drawWaterway(context, waterway);
  strokeCenterline(context, track, track.data.mean_width + 16, "rgba(3, 11, 10, .45)");
  strokeCenterline(context, track, track.data.mean_width + 8, "#c9b69a");
  strokeCenterline(context, track, track.data.mean_width, "#272e2f");
  strokeCenterline(context, track, track.data.mean_width - 9, "#303839");
  context.setLineDash([18, 16]);
  strokeCenterline(context, track, 2.2, "rgba(224, 230, 218, .32)");
  context.setLineDash([]);
  drawCurbs(context, track.leftBoundary);
  drawCurbs(context, track.rightBoundary);
  for (const [x, y, radius] of track.openJunctions) {
    context.beginPath(); context.arc(x, y, radius + 4, 0, Math.PI * 2);
    context.fillStyle = "#303839"; context.fill();
  }
  drawStartLine(context, track);
  for (const ramp of track.ramps) {
    const halfWidth = Math.min(40, ramp.lateral_tolerance);
    const distance = ramp.end_progress - ramp.start_progress;
    const steps = Math.max(4, Math.ceil(distance / 8));
    const left: Vec2[] = [];
    const right: Vec2[] = [];
    for (let index = 0; index <= steps; index += 1) {
      const fraction = index / steps;
      const sample = track.pointAt(ramp.start_progress + distance * fraction);
      const normal: Vec2 = [-Math.sin(sample.heading), Math.cos(sample.heading)];
      const width = halfWidth * (1 - fraction * 0.2);
      left.push([sample.point[0] + normal[0] * width, sample.point[1] + normal[1] * width]);
      right.push([sample.point[0] - normal[0] * width, sample.point[1] - normal[1] * width]);
    }
    const rampShape = [...left, ...[...right].reverse()];
    const exit = track.pointAt(ramp.end_progress);
    const exitForward: Vec2 = [Math.cos(exit.heading), Math.sin(exit.heading)];
    const exitNormal: Vec2 = [-Math.sin(exit.heading), Math.cos(exit.heading)];
    const exitWidth = halfWidth * .8;
    const landingShadow: Vec2[] = [
      [exit.point[0] - exitNormal[0] * exitWidth, exit.point[1] - exitNormal[1] * exitWidth],
      [exit.point[0] + exitNormal[0] * exitWidth, exit.point[1] + exitNormal[1] * exitWidth],
      [exit.point[0] + exitNormal[0] * exitWidth + exitForward[0] * 20, exit.point[1] + exitNormal[1] * exitWidth + exitForward[1] * 20],
      [exit.point[0] - exitNormal[0] * exitWidth + exitForward[0] * 20, exit.point[1] - exitNormal[1] * exitWidth + exitForward[1] * 20],
    ];
    pathClosed(context, landingShadow);
    context.fillStyle = "rgba(2, 8, 8, .54)";
    context.fill();
    context.save();
    context.translate(5, 7);
    pathClosed(context, rampShape);
    context.fillStyle = "rgba(3, 9, 10, .7)";
    context.fill();
    context.restore();
    const entry = track.pointAt(ramp.start_progress);
    const rampGradient = context.createLinearGradient(entry.point[0], entry.point[1], exit.point[0], exit.point[1]);
    rampGradient.addColorStop(0, "#424a4e");
    rampGradient.addColorStop(0.58, "#657176");
    rampGradient.addColorStop(1, "#89969a");
    pathClosed(context, rampShape);
    context.fillStyle = rampGradient;
    context.fill();
    context.strokeStyle = "#f5bc45";
    context.lineWidth = 4;
    context.stroke();
    strokeOpenPath(context, left, 3, "#ffe080");
    strokeOpenPath(context, right, 3, "#ffe080");
    for (let stripe = 1; stripe < 5; stripe += 1) {
      const fraction = stripe / 5;
      const progress = ramp.start_progress + distance * fraction;
      const sample = track.pointAt(progress);
      const normal: Vec2 = [-Math.sin(sample.heading), Math.cos(sample.heading)];
      const width = halfWidth * (1 - fraction * 0.2) - 5;
      context.beginPath();
      context.moveTo(sample.point[0] - normal[0] * width, sample.point[1] - normal[1] * width);
      context.lineTo(sample.point[0] + normal[0] * width, sample.point[1] + normal[1] * width);
      context.strokeStyle = stripe % 2 === 0 ? "rgba(224, 234, 231, .86)" : "rgba(92, 108, 110, .72)";
      context.lineWidth = 3;
      context.stroke();
    }
    context.beginPath();
    context.moveTo(exit.point[0] - exitNormal[0] * exitWidth, exit.point[1] - exitNormal[1] * exitWidth);
    context.lineTo(exit.point[0] + exitNormal[0] * exitWidth, exit.point[1] + exitNormal[1] * exitWidth);
    context.strokeStyle = "#fff0b0";
    context.lineWidth = 6;
    context.stroke();
  }
  for (const surface of track.surfaces) {
    const entry = track.pointAt(surface.start_progress);
    const exit = track.pointAt(surface.end_progress);
    const normal: Vec2 = [-Math.sin(entry.heading), Math.cos(entry.heading)];
    const corners: Vec2[] = [
      [entry.point[0] - normal[0] * surface.lateral_tolerance, entry.point[1] - normal[1] * surface.lateral_tolerance],
      [entry.point[0] + normal[0] * surface.lateral_tolerance, entry.point[1] + normal[1] * surface.lateral_tolerance],
      [exit.point[0] + normal[0] * surface.lateral_tolerance, exit.point[1] + normal[1] * surface.lateral_tolerance],
      [exit.point[0] - normal[0] * surface.lateral_tolerance, exit.point[1] - normal[1] * surface.lateral_tolerance],
    ];
    pathClosed(context, corners); context.fillStyle = "rgba(44,174,214,.52)"; context.fill();
    context.strokeStyle = "rgba(185,239,251,.72)"; context.lineWidth = 2;
    for (let stripe = 1; stripe < 6; stripe += 1) {
      const fraction = stripe / 6;
      const center: Vec2 = [
        entry.point[0] + (exit.point[0] - entry.point[0]) * fraction,
        entry.point[1] + (exit.point[1] - entry.point[1]) * fraction,
      ];
      context.beginPath();
      context.moveTo(center[0] - normal[0] * surface.lateral_tolerance * .72, center[1] - normal[1] * surface.lateral_tolerance * .72);
      context.lineTo(center[0] + normal[0] * surface.lateral_tolerance * .72, center[1] + normal[1] * surface.lateral_tolerance * .72);
      context.stroke();
    }
  }
  for (const tornado of track.tornadoes) {
    const entry = track.pointAt(tornado.start_progress);
    const exit = track.pointAt(tornado.end_progress);
    const midpoint: Vec2 = [(entry.point[0] + exit.point[0]) / 2, (entry.point[1] + exit.point[1]) / 2];
    const normal: Vec2 = [-Math.sin(entry.heading), Math.cos(entry.heading)];
    const corners: Vec2[] = [
      [entry.point[0] + normal[0] * 27, entry.point[1] + normal[1] * 27],
      [entry.point[0] - normal[0] * 27, entry.point[1] - normal[1] * 27],
      [exit.point[0] - normal[0] * 27, exit.point[1] - normal[1] * 27],
      [exit.point[0] + normal[0] * 27, exit.point[1] + normal[1] * 27],
    ];
    pathClosed(context, corners); context.fillStyle = "rgba(119, 73, 168, .28)"; context.fill();
    for (const side of [-1, 1]) {
      const base: Vec2 = [entry.point[0] + normal[0] * 34 * side, entry.point[1] + normal[1] * 34 * side];
      context.beginPath(); context.arc(base[0] + 5, base[1] + 6, 9, 0, Math.PI * 2); context.fillStyle = "rgba(5,10,11,.55)"; context.fill();
      context.beginPath(); context.arc(base[0], base[1], 7, 0, Math.PI * 2); context.fillStyle = "#c189ff"; context.fill();
      context.beginPath(); context.arc(base[0], base[1], 3, 0, Math.PI * 2); context.fillStyle = "#273235"; context.fill();
    }
    for (const radius of [25, 17, 9]) {
      context.beginPath(); context.arc(midpoint[0], midpoint[1], radius, -Math.PI * 0.35, Math.PI * 1.2);
      context.strokeStyle = radius === 25 ? "#d7adff" : "rgba(215,173,255,.72)";
      context.lineWidth = 3; context.stroke();
    }
    const label: Vec2 = [midpoint[0] + normal[0] * 48, midpoint[1] + normal[1] * 48];
    context.save(); context.translate(label[0], label[1]); context.rotate(-viewRotation);
    context.fillStyle = "rgba(8,17,19,.92)"; context.strokeStyle = "#c189ff"; context.lineWidth = 1;
    context.beginPath(); context.roundRect(-72, -11, 144, 22, 5); context.fill(); context.stroke();
    context.fillStyle = "#d7adff"; context.font = "800 10px system-ui"; context.textAlign = "center"; context.textBaseline = "middle";
    context.fillText("TORNADO · FULL SPEED", 0, 0.5); context.restore();
  }
  for (const obstacle of track.obstacles) {
    const corners = obstacleCorners(obstacle);
    context.save();
    context.translate(4, 5);
    pathClosed(context, corners);
    context.fillStyle = "rgba(3, 8, 8, .55)";
    context.fill();
    context.restore();
    pathClosed(context, corners);
    context.fillStyle = "#d96337";
    context.fill();
    context.strokeStyle = "#f7d79c";
    context.lineWidth = 3;
    context.stroke();
  }
  for (const bridge of track.bridges) drawBridgeUnderlay(context, track, bridge);
  for (const bridge of track.bridges) drawBridgeDeck(context, track, bridge);
}

function drawBridgeOverlay(context: CanvasRenderingContext2D, track: TrackRuntime): void {
  // Broen ligger i grundtegningen, men tegnes igen mellem jord- og brotrafik.
  // Dermed forsvinder biler på den krydsende gren faktisk under dækket.
  for (const bridge of track.bridges) {
    drawBridgeDeck(context, track, bridge, true, false);
  }
}

function drawBridgeRailsOverlay(context: CanvasRenderingContext2D, track: TrackRuntime): void {
  for (const bridge of track.bridges) drawBridgeRails(context, track, bridge);
}

function drawRabbit(
  context: CanvasRenderingContext2D,
  position: Vec2,
  heading: number,
  visual: RabbitVisualState,
): void {
  context.save();
  context.translate(position[0], position[1]);
  // Sideprofilen må spejle vandret, men hoved og ører bliver altid oprejst.
  const pose = uprightRabbitTransform(heading);
  context.scale(pose.facing, 1);
  context.rotate(pose.tilt);
  context.fillStyle = `rgba(0, 0, 0, ${visual.shadowAlpha})`;
  context.beginPath();
  context.ellipse(1, 4, 7 * visual.shadowScale, 3.5 * visual.shadowScale, 0, 0, Math.PI * 2);
  context.fill();
  context.translate(visual.forwardOffset, -visual.lift);
  context.rotate(visual.rotation);
  context.scale(visual.scaleX, visual.scaleY);
  context.fillStyle = "#f5f2e9";
  context.beginPath();
  context.ellipse(-0.8, 0, 6.5, 4.25, 0, 0, Math.PI * 2);
  context.fill();
  context.fillStyle = "#d9d4c8";
  context.beginPath();
  context.arc(-6.2, -0.4, 2.15, 0, Math.PI * 2);
  context.fill();
  context.fillStyle = "#f5f2e9";
  context.beginPath();
  context.ellipse(5.1, -1.1, 3.5, 3.15, 0, 0, Math.PI * 2);
  context.fill();
  context.fillStyle = "#f5f2e9";
  context.beginPath();
  context.ellipse(4.5, -6.1, 1.25, 4.1, -0.16, 0, Math.PI * 2);
  context.ellipse(7, -5.8, 1.15, 3.8, 0.18, 0, Math.PI * 2);
  context.fill();
  context.strokeStyle = "rgba(212, 125, 132, .55)";
  context.lineWidth = 0.65;
  context.beginPath();
  context.moveTo(4.7, -4.3);
  context.lineTo(4.4, -7.6);
  context.moveTo(6.8, -4.1);
  context.lineTo(7.2, -7.2);
  context.stroke();
  context.fillStyle = "#1c2422";
  context.beginPath();
  context.arc(7.1, -1.8, 0.75, 0, Math.PI * 2);
  context.fill();
  context.fillStyle = "#d85d63";
  context.beginPath();
  context.arc(8.5, -0.6, 0.65, 0, Math.PI * 2);
  context.fill();
  context.restore();
}

function drawDecal(context: CanvasRenderingContext2D, position: Vec2, heading: number): void {
  context.save();
  context.translate(position[0], position[1]);
  context.rotate(heading);
  context.globalAlpha = 0.7;
  context.fillStyle = "#e7ff71";
  context.beginPath();
  for (let point = 0; point < 12; point += 1) {
    const angle = point * Math.PI / 6;
    const radius = point % 2 ? 4 : 8;
    const x = Math.cos(angle) * radius;
    const y = Math.sin(angle) * radius;
    if (point === 0) context.moveTo(x, y); else context.lineTo(x, y);
  }
  context.closePath();
  context.fill();
  context.fillStyle = "#15221d";
  context.font = "700 6px system-ui";
  context.textAlign = "center";
  context.fillText("!", 0, 2);
  context.restore();
}

function drawVehicleShape(context: CanvasRenderingContext2D, vehicle: VehicleData, color: string): void {
  const { car_length: carLength, car_width: carWidth } = vehicle.physics;
  if (vehicle.key !== "aero") {
    context.fillStyle = "#050908";
    context.fillRect(-carLength * 0.34, -carWidth * 0.61, carLength * 0.2, 3);
    context.fillRect(-carLength * 0.34, carWidth * 0.42, carLength * 0.2, 3);
    context.fillRect(carLength * 0.17, -carWidth * 0.61, carLength * 0.2, 3);
    context.fillRect(carLength * 0.17, carWidth * 0.42, carLength * 0.2, 3);
  }
  context.fillStyle = color;
  context.strokeStyle = "#07100f";
  context.lineWidth = 2;
  if (vehicle.key === "aero") {
    context.fillRect(-carLength * 0.48, -carWidth * 0.55, 4, carWidth * 1.1);
    context.fillRect(carLength * 0.35, -carWidth * 0.7, 3.5, carWidth * 1.4);
    context.beginPath();
    context.moveTo(-carLength * 0.38, -carWidth * 0.28);
    context.lineTo(carLength * 0.48, -carWidth * 0.12);
    context.lineTo(carLength * 0.48, carWidth * 0.12);
    context.lineTo(-carLength * 0.38, carWidth * 0.28);
    context.closePath();
    context.fill(); context.stroke();
    context.fillStyle = "#111a18";
    context.fillRect(-8, -carWidth * 0.62, 7, 3);
    context.fillRect(-8, carWidth * 0.42, 7, 3);
    context.fillRect(7, -carWidth * 0.62, 7, 3);
    context.fillRect(7, carWidth * 0.42, 7, 3);
  } else if (vehicle.key === "tank") {
    context.beginPath();
    context.moveTo(-carLength / 2, -carWidth * 0.42);
    context.lineTo(-carLength * 0.28, -carWidth / 2);
    context.lineTo(carLength * 0.42, -carWidth / 2);
    context.lineTo(carLength / 2, -carWidth * 0.28);
    context.lineTo(carLength / 2, carWidth * 0.28);
    context.lineTo(carLength * 0.42, carWidth / 2);
    context.lineTo(-carLength * 0.28, carWidth / 2);
    context.lineTo(-carLength / 2, carWidth * 0.42);
    context.closePath(); context.fill(); context.stroke();
    context.fillStyle = "rgba(7, 16, 15, .65)";
    context.fillRect(-4, -carWidth * 0.38, 10, carWidth * 0.76);
  } else if (vehicle.key === "agile") {
    context.beginPath();
    context.roundRect(-carLength / 2, -carWidth / 2, carLength, carWidth, 5);
    context.fill(); context.stroke();
    context.fillStyle = "rgba(7, 16, 15, .7)";
    context.beginPath();
    context.roundRect(-4, -carWidth * 0.38, 11, carWidth * 0.76, 3);
    context.fill();
  } else {
    context.beginPath();
    context.moveTo(-carLength / 2, -carWidth * 0.38);
    context.quadraticCurveTo(-carLength * 0.36, -carWidth / 2, -carLength * 0.12, -carWidth / 2);
    context.lineTo(carLength * 0.35, -carWidth * 0.42);
    context.quadraticCurveTo(carLength / 2, -carWidth * 0.25, carLength / 2, 0);
    context.quadraticCurveTo(carLength / 2, carWidth * 0.25, carLength * 0.35, carWidth * 0.42);
    context.lineTo(-carLength * 0.12, carWidth / 2);
    context.quadraticCurveTo(-carLength * 0.36, carWidth / 2, -carLength / 2, carWidth * 0.38);
    context.closePath(); context.fill(); context.stroke();
    context.fillStyle = "rgba(7, 16, 15, .68)";
    context.beginPath();
    context.roundRect(-4, -carWidth * 0.37, 10, carWidth * 0.74, 2);
    context.fill();
  }
  const bodyHighlight = context.createLinearGradient(-carLength / 2, -carWidth / 2, carLength / 2, carWidth / 2);
  bodyHighlight.addColorStop(0, "rgba(255,255,255,.34)");
  bodyHighlight.addColorStop(.35, "rgba(255,255,255,.08)");
  bodyHighlight.addColorStop(1, "rgba(0,0,0,.22)");
  context.fillStyle = bodyHighlight;
  context.beginPath();
  context.roundRect(-carLength * .38, -carWidth * .34, carLength * .72, carWidth * .68, Math.max(2, carWidth * .18));
  context.fill();
  context.fillStyle = "rgba(7,16,15,.82)";
  context.beginPath();
  context.roundRect(-carLength * .11, -carWidth * .31, carLength * .26, carWidth * .62, 2);
  context.fill();
  context.strokeStyle = "rgba(235,246,239,.35)"; context.lineWidth = 1;
  context.beginPath(); context.moveTo(-carLength * .05, -carWidth * .3); context.lineTo(-carLength * .05, carWidth * .3); context.stroke();
  context.fillStyle = "rgba(255,255,255,.42)";
  context.fillRect(-carLength * .31, -carWidth * .06, carLength * .52, Math.max(1.2, carWidth * .08));
  context.fillStyle = "#f3ffe2";
  context.fillRect(vehicle.physics.car_length * 0.32, -carWidth * 0.28, 2, 3);
  context.fillRect(vehicle.physics.car_length * 0.32, carWidth * 0.08, 2, 3);
  context.fillStyle = "#ff594d";
  context.fillRect(-vehicle.physics.car_length * 0.38, -carWidth * 0.28, 2, 3);
  context.fillRect(-vehicle.physics.car_length * 0.38, carWidth * 0.08, 2, 3);
}

function tintedVehicleSprite(
  participant: RaceParticipant,
  source: HTMLImageElement,
): HTMLCanvasElement {
  const key = `${participant.vehicle.key}:${participant.selection.color.join("-")}`;
  const cached = tintedVehicleSpriteCache.get(key);
  if (cached) return cached;
  const canvas = document.createElement("canvas");
  canvas.width = source.naturalWidth;
  canvas.height = source.naturalHeight;
  const context = canvas.getContext("2d");
  if (context) {
    context.drawImage(source, 0, 0);
    context.globalCompositeOperation = "source-atop";
    context.globalAlpha = .48;
    context.fillStyle = rgb(participant.selection.color);
    context.fillRect(0, 0, canvas.width, canvas.height);
  }
  tintedVehicleSpriteCache.set(key, canvas);
  return canvas;
}

function drawCar(
  context: CanvasRenderingContext2D,
  participant: RaceParticipant,
  viewRotation = 0,
  onReady: () => void = () => undefined,
): boolean {
  context.save();
  context.translate(participant.car.position[0], participant.car.position[1]);
  context.rotate(participant.car.heading);
  const visualLift = participant.car.onBridge ? 0 : participant.car.z * 0.55;
  const shadowScale = participant.car.onBridge ? 1 : Math.max(0.55, 1 - participant.car.z / 180);
  const shadowAlpha = participant.car.onBridge ? 0.38 : Math.max(0.1, 0.38 - participant.car.z / 190);
  context.fillStyle = `rgba(0,0,0,${shadowAlpha})`;
  context.beginPath();
  context.ellipse(-3, 7, participant.vehicle.physics.car_length * 0.72 * shadowScale, participant.vehicle.physics.car_width * 0.78 * shadowScale, 0, 0, Math.PI * 2);
  context.fill();
  if (participant.car.z > 1 && !participant.car.onBridge) {
    context.beginPath(); context.moveTo(0, 3); context.lineTo(0, -visualLift);
    context.setLineDash([3, 4]); context.strokeStyle = "rgba(231,255,113,.42)"; context.lineWidth = 1.2; context.stroke(); context.setLineDash([]);
  }
  context.translate(0, -visualLift);
  const liftScale = participant.car.onBridge
    ? 1 + Math.min(0.15, participant.car.z / 220)
    : vehicleAltitudeScale(participant.car.z);
  const source = vehicleSprite(participant.vehicle.key, onReady);
  context.scale(liftScale, liftScale);
  if (source) {
    const sprite = tintedVehicleSprite(participant, source);
    context.rotate(Math.PI / 2);
    const width = participant.vehicle.physics.car_width * 1.5;
    const height = participant.vehicle.physics.car_length * 1.5;
    context.drawImage(sprite, -width / 2, -height / 2, width, height);
  } else {
    context.scale(1.5, 1.5);
    drawVehicleShape(context, participant.vehicle, rgb(participant.selection.color));
  }
  context.restore();
  context.save();
  context.translate(participant.car.position[0], participant.car.position[1]);
  context.rotate(-viewRotation);
  context.translate(0, -visualLift - 24 * liftScale);
  const badge = participant.driver?.badge ?? "YOU";
  context.font = "800 8px system-ui";
  context.textAlign = "center";
  context.textBaseline = "middle";
  context.fillStyle = "rgba(5, 13, 12, .88)";
  context.beginPath();
  context.roundRect(-13, -6, 26, 12, 5);
  context.fill();
  context.fillStyle = "#f4f5ec";
  context.fillText(badge, 0, 0.5);
  context.restore();
  return source !== null;
}

interface TornadoTarget {
  tornado: TornadoData;
  active: boolean;
  approachDistance: number;
}

function tornadoTarget(engine: QuickRaceEngine, participant: RaceParticipant): TornadoTarget | null {
  const active = engine.track.tornadoes.find((tornado) => participant.car.stuntSegmentId === tornado.id);
  if (active) return { tornado: active, active: true, approachDistance: 0 };
  if (engine.track.tornadoes.length === 0) return null;
  const progress = engine.track.project(participant.car.position).progress;
  const candidates = engine.track.tornadoes.map((tornado) => ({
    tornado,
    active: false,
    approachDistance: (tornado.start_progress - progress + engine.track.length) % engine.track.length,
  }));
  const nearest = candidates.sort((first, second) => first.approachDistance - second.approachDistance)[0];
  return nearest && nearest.approachDistance <= 180 ? nearest : null;
}

function drawTornadoHud(context: CanvasRenderingContext2D, participant: RaceParticipant, target: TornadoTarget, fullSpinSpeed: number): void {
  const width = Math.min(330, context.canvas.clientWidth - 36);
  const height = 142;
  const compact = context.canvas.clientWidth <= 760;
  const touchOffset = context.canvas.clientWidth <= 1100 ? 92 : 18;
  const x = 18;
  const y = compact ? 84 : Math.max(84, context.canvas.clientHeight - height - touchOffset);
  context.save();
  context.fillStyle = "rgba(7,15,18,.93)";
  context.strokeStyle = "#c189ff";
  context.lineWidth = 2;
  context.beginPath(); context.roundRect(x, y, width, height, 10); context.fill(); context.stroke();
  const theta = target.active ? participant.car.stuntProgress : 0;
  const angle = Math.min(360, theta * 180 / Math.PI);
  const predicted = Math.min(360, participant.car.stuntSpeed / fullSpinSpeed * 360);
  context.fillStyle = "#d7adff"; context.font = "800 13px system-ui"; context.textAlign = "left";
  context.fillText(target.active ? "TORNADO · SPINNING" : "TORNADO AHEAD", x + 20, y + 28);
  context.fillStyle = "#e8eee7"; context.font = "700 13px ui-monospace";
  context.fillText(target.active ? `${angle.toFixed(0).padStart(3, "0")}° / 360°` : `entry in ${target.approachDistance.toFixed(0)}`, x + 20, y + 58);
  context.fillStyle = "rgba(255,255,255,.12)"; context.fillRect(x + 20, y + 74, width - 40, 10);
  context.fillStyle = predicted >= 358 ? "#e7ff71" : "#c189ff";
  context.fillRect(x + 20, y + 74, (width - 40) * (target.active ? angle : 0) / 360, 10);
  context.fillStyle = "#aebdb9";
  context.fillText(target.active ? `entry speed ${participant.car.stuntSpeed.toFixed(1)} · exit ${predicted.toFixed(0)}°` : `Full speed (${fullSpinSpeed.toFixed(0)}+) = clean 360° exit`, x + 20, y + 110);
  context.restore();
}

function drawRays(context: CanvasRenderingContext2D, participant: RaceParticipant): void {
  context.save();
  context.lineWidth = 1.25;
  for (const reading of participant.readings) {
    context.beginPath();
    context.moveTo(participant.car.position[0], participant.car.position[1]);
    context.lineTo(reading.endpoint[0], reading.endpoint[1]);
    context.strokeStyle = reading.hitType === "RABBIT" ? "rgba(231,255,113,.8)" : reading.hitType === "VEHICLE" ? "rgba(255,124,88,.8)" : "rgba(87,217,195,.22)";
    context.stroke();
  }
  context.restore();
}

export class RaceCanvasRenderer {
  private readonly canvas: HTMLCanvasElement;
  private readonly worldSize: Vec2;

  constructor(canvas: HTMLCanvasElement, worldSize: Vec2) {
    this.canvas = canvas;
    this.worldSize = worldSize;
  }

  render(engine: QuickRaceEngine, focusedParticipantId: string | null = null): void {
    const context = setupCanvas(this.canvas);
    context.clearRect(0, 0, this.canvas.clientWidth, this.canvas.clientHeight);
    context.fillStyle = "#123226";
    context.fillRect(0, 0, this.canvas.clientWidth, this.canvas.clientHeight);
    const focused = focusedParticipantId
      ? engine.participants.find((participant) => participant.selection.slotId === focusedParticipantId) ?? null
      : null;
    const transform = transformFor(
      this.canvas,
      trackBounds(engine.track, this.worldSize),
      focused?.car.position ?? null,
    );
    this.canvas.dataset.following = focused?.selection.slotId ?? "overview";
    this.canvas.dataset.orientation = transform.rotation === 0 ? "landscape" : "portrait-rotated";
    context.save();
    applyWorld(context, transform);
    const wildernessReady = drawInfiniteWilderness(
      context,
      () => this.render(engine, focusedParticipantId),
    );
    this.canvas.dataset.wilderness = wildernessReady ? "textured" : "loading";
    const usesSharedBackground = drawSharedTrackBackground(
      context,
      engine.track,
      () => this.render(engine, focusedParticipantId),
    );
    if (!usesSharedBackground) {
      drawEnvironment(context, engine.track, this.worldSize, focused !== null);
      drawTrack(context, engine.track, transform.rotation);
    }
    for (const decal of engine.rabbitWorld.decals) drawDecal(context, decal.position, decal.heading);
    const latestIncident = new Map<string, number>();
    for (const decal of engine.rabbitWorld.decals) latestIncident.set(decal.rabbitId, decal.elapsedSeconds);
    const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    for (const rabbit of engine.rabbitWorld.rabbits) {
      const incidentAt = latestIncident.get(rabbit.id);
      const incidentAge = incidentAt === undefined ? null : engine.rabbitWorld.elapsed - incidentAt;
      const visual = rabbitVisualState(
        rabbit.id,
        engine.selection.raceSeed,
        engine.rabbitWorld.elapsed,
        reducedMotion,
        incidentAge,
      );
      drawRabbit(context, rabbit.position, rabbit.heading, visual);
    }
    const leader = engine.results[0];
    const selected = focused ?? engine.participants.find((participant) => participant.selection.slotId === leader?.participantId);
    const activeTornado = selected ? tornadoTarget(engine, selected) : null;
    this.canvas.dataset.tornado = activeTornado?.active ? "spin" : activeTornado ? "approach" : "off";
    if (selected) drawRays(context, selected);
    let rasterCarsReady = true;
    for (const participant of engine.participants) {
      if (!participant.car.onBridge) {
        rasterCarsReady = drawCar(
          context,
          participant,
          transform.rotation,
          () => this.render(engine, focusedParticipantId),
        ) && rasterCarsReady;
      }
    }
    if (!drawSharedTrackForeground(
      context,
      engine.track,
      () => this.render(engine, focusedParticipantId),
    )) {
      drawBridgeOverlay(context, engine.track);
    }
    for (const participant of engine.participants) {
      if (participant.car.onBridge) {
        rasterCarsReady = drawCar(
          context,
          participant,
          transform.rotation,
          () => this.render(engine, focusedParticipantId),
        ) && rasterCarsReady;
      }
    }
    if (!drawSharedTrackRails(
      context,
      engine.track,
      () => this.render(engine, focusedParticipantId),
    )) {
      drawBridgeRailsOverlay(context, engine.track);
    }
    this.canvas.dataset.carSprites = rasterCarsReady ? "raster" : "loading";
    for (const impact of engine.recentImpactEvents) {
      const age = Math.max(0, engine.elapsedSeconds - impact.elapsedSeconds);
      const radius = 13 + age * 80;
      const alpha = Math.max(0, 1 - age / 0.28);
      context.beginPath();
      context.arc(impact.position[0], impact.position[1], radius, 0, Math.PI * 2);
      context.lineWidth = 5 * alpha;
      context.strokeStyle = `rgba(255, 199, 84, ${alpha})`;
      context.stroke();
      for (let spark = 0; spark < 6; spark += 1) {
        const angle = spark * Math.PI / 3 + impact.elapsedSeconds * 2.7;
        context.beginPath();
        context.moveTo(impact.position[0] + Math.cos(angle) * radius * 0.58, impact.position[1] + Math.sin(angle) * radius * 0.58);
        context.lineTo(impact.position[0] + Math.cos(angle) * radius, impact.position[1] + Math.sin(angle) * radius);
        context.lineWidth = 1.6 * alpha;
        context.strokeStyle = `rgba(255, 224, 129, ${alpha})`;
        context.stroke();
      }
    }
    context.restore();
    if (selected && activeTornado) drawTornadoHud(
      context,
      selected,
      activeTornado,
      engine.data.game_semantics.stunts.tornado_full_spin_speed,
    );
  }
}

export function drawTrackPreview(canvas: HTMLCanvasElement, data: TrackData): void {
  const context = setupCanvas(canvas);
  const world: Vec2 = [940, 920];
  const track = new TrackRuntime(data);
  context.clearRect(0, 0, canvas.clientWidth, canvas.clientHeight);
  const transform = transformFor(canvas, trackBounds(track, world));
  context.save();
  applyWorld(context, transform);
  if (drawSharedTrackBackground(context, track, () => drawTrackPreview(canvas, data))) {
    context.restore();
    return;
  }
  context.fillStyle = "#10261f";
  context.fillRect(0, 0, world[0], world[1]);
  strokeCenterline(context, track, data.mean_width + 9, "#bca98b");
  strokeCenterline(context, track, data.mean_width, "#303838");
  strokeCenterline(context, track, 3, "rgba(231, 255, 113, .48)");
  for (const surface of track.surfaces) {
    const start = track.pointAt(surface.start_progress).point;
    const end = track.pointAt(surface.end_progress).point;
    context.beginPath(); context.moveTo(start[0], start[1]); context.lineTo(end[0], end[1]);
    context.strokeStyle = "rgba(71,195,231,.9)"; context.lineWidth = Math.min(data.mean_width * .7, surface.lateral_tolerance * 1.5); context.stroke();
  }
  for (const ramp of track.ramps) {
    const start = track.pointAt(ramp.start_progress).point;
    const end = track.pointAt(ramp.end_progress).point;
    context.beginPath(); context.moveTo(start[0], start[1]); context.lineTo(end[0], end[1]);
    context.strokeStyle = "#f6be3a"; context.lineWidth = 15; context.stroke();
  }
  for (const bridge of track.bridges) {
    const points = sampledTrackPath(track, bridge.start_progress, bridge.end_progress);
    strokeOpenPath(context, points, data.mean_width * .82 + 7, "#e1ca71");
    strokeOpenPath(context, points, data.mean_width * .82, "#4c5257");
  }
  for (const obstacle of track.obstacles) {
    context.beginPath(); context.arc(obstacle.center[0], obstacle.center[1], 10, 0, Math.PI * 2);
    context.fillStyle = "#ff7c58"; context.fill();
  }
  context.restore();
}

export function drawVehiclePreview(canvas: HTMLCanvasElement, vehicle: VehicleData, color: [number, number, number]): void {
  const context = setupCanvas(canvas);
  context.clearRect(0, 0, canvas.clientWidth, canvas.clientHeight);
  const sprite = vehicleSprite(vehicle.key, () => drawVehiclePreview(canvas, vehicle, color));
  if (sprite) {
    const padding = Math.max(8, Math.min(canvas.clientWidth, canvas.clientHeight) * 0.08);
    const scale = Math.min(
      (canvas.clientWidth - padding * 2) / sprite.naturalWidth,
      (canvas.clientHeight - padding * 2) / sprite.naturalHeight,
    );
    const width = sprite.naturalWidth * scale;
    const height = sprite.naturalHeight * scale;
    const x = (canvas.clientWidth - width) / 2;
    const y = (canvas.clientHeight - height) / 2;
    context.save();
    context.drawImage(sprite, x, y, width, height);
    context.globalCompositeOperation = "source-atop";
    context.globalAlpha = 0.66;
    context.fillStyle = rgb(color);
    context.fillRect(x, y, width, height);
    context.restore();
    canvas.dataset.spriteReady = vehicle.key;
    return;
  }
  delete canvas.dataset.spriteReady;
  context.save();
  context.translate(canvas.clientWidth / 2, canvas.clientHeight / 2);
  context.scale(3, 3);
  context.rotate(-0.08);
  drawVehicleShape(context, vehicle, rgb(color));
  context.restore();
}
